// StepUploadFiles.tsx
import React from "react";
import DragAndDrop from "./DragAndDrop.tsx";
import {ALLOWED_TYPES} from "../../models/constants/constants.ts";
import {SanitizedPreset} from "../../utils/presetSanitizer.ts";
import type {SubtitleStep} from "./LanguagesStep.tsx";

interface StepUploadFilesProps {
  uploadedFiles: File[];
  customConfigurationId?: string;
  selectedSourceLanguage: string;
  setUploadedFiles: (files: File[]) => void;
  fileNameToSeriesNameRelation: Record<string, string>;
  setFileNameToSeriesNameRelation: (relation: Record<string, string>) => void;
  setCustomConfiguration: (configId: string | undefined) => void;
  applyPreset: (preset: SanitizedPreset) => void;
  goToSubtitleStep: (step: SubtitleStep) => void;
  /** `null` while the balance is loading; `Infinity` for an unlimited workspace. */
  remainingCredits: number | null;
  setFileDuration: (fileName: string, seconds: number) => void;
  renameFileDuration: (from: string, to: string) => void;
  removeFileDuration: (fileName: string) => void;
  handleErrors: (error: string, action?: "add" | "remove") => void;
}

const StepUploadFiles: React.FC<StepUploadFilesProps> = (
  {
    uploadedFiles,
    customConfigurationId,
    selectedSourceLanguage,
    setUploadedFiles,
    fileNameToSeriesNameRelation,
    setFileNameToSeriesNameRelation,
    setCustomConfiguration,
    applyPreset,
    goToSubtitleStep,
    remainingCredits,
    setFileDuration,
    renameFileDuration,
    removeFileDuration,
    handleErrors,
  }:StepUploadFilesProps) => {
  // Balance note only: targets are not chosen yet, so the budget verdict lives on the final
  // subtitle substep and in the not-enough dialog.
  const creditsNote: string | null = remainingCredits === null
    ? null
    : remainingCredits === Infinity
      ? "* Unlimited credits."
      : `* You have ${remainingCredits} credits remaining.`;

  return (
    <div className='flex flex-col flex-1 min-h-0'>
      <DragAndDrop
        uploadFiles={uploadedFiles}
        customConfigurationId={customConfigurationId}
        selectedSourceLanguage={selectedSourceLanguage}
        setUploadedFiles={setUploadedFiles}
        fileNameToSeriesNameRelation={fileNameToSeriesNameRelation}
        setFileNameToSeriesNameRelation={setFileNameToSeriesNameRelation}
        setCustomConfiguration={setCustomConfiguration}
        applyPreset={applyPreset}
        goToSubtitleStep={goToSubtitleStep}
        setFileDuration={setFileDuration}
        renameFileDuration={renameFileDuration}
        removeFileDuration={removeFileDuration}
        handleErrors={handleErrors}
      />
      <div className="mt-auto flex flex-col gap-1.5 pt-6">
        {creditsNote !== null && (
          <div className="my-0 text-sm text-loginInputsText-500 text-pretty">
            {creditsNote}
          </div>
        )}
        <div className="text-xs text-n-400">
          * Supported formats: {Object.values(ALLOWED_TYPES).join(", ")}
        </div>
        <div className="text-xs text-n-400">
          * Upload limit: 4GB per file
        </div>
      </div>
    </div>
  );
};

export default StepUploadFiles;
